"use client";

// THE STAGE — the one layout every navigator surface stands on (law_stage).
//
// The screen reads in two parts: the NAVIGATOR on top, as tall as what it is
// showing (a title strip, a line about the place, the view chips), and the
// CONTENT below it, scrolling in its own box. The content lets go at the
// navigator's edge (a mask in the scroller's own frame, so it never trails a
// fast scroll the way the old viewport-framed seam did) and a thin progressive
// blur sits on that edge: the words soften as they go under the navigator, and
// simply end there. When the navigator folds, the content's visible area grows
// by exactly what it gave up; when the place has more to say, it shrinks; when
// the content fits, nothing scrolls at all.
//
// TWO WAYS TO HOLD THE SPLIT (`fold`):
//   · "layer" (the default) — the scroller covers the whole stage and the
//     navigator lies over its top, its box never changing. The content is
//     padded below the navigator's measured height and the mask's edge sits on
//     that line, moving up as the navigator folds. Nothing under the navigator
//     ever moves (law_scroll_lock, the detached fold), a hover opens the
//     navigator OVER the content, a wheel or a drag on the navigator's empty
//     ground reaches the content, and the scrollbar runs the full height,
//     exactly as the page's did.
//   · "split" — the navigator is a row of its own and the scroller is the row
//     below it: the navigator's height really sets the scroll area's, and a fold
//     really grows it (the content rides up with it). Kept to compare.
//
// Everything that floats (the crumb bar, the ≡ menu, the share/options dock,
// the ground dial, the corners, sheets) stays fixed OUTSIDE the stage, and
// netti is painted before it. The stage is one stacking context at z 1, where
// the old content surface was.
//
// Moving to another place hands the page over (lib/stage/handoff): the old view
// (and, at a new level, the old navigator) drifts away toward the side you left
// by while the new one arrives from the side you are heading. The scroller is
// the page's scroller for everything that asks (lib/stage/scroller), so the
// reading ramp, the fold and every scroll-led view follow it.

import "./stage.css";
import { Component, createRef, useEffect, useLayoutEffect, useRef, type CSSProperties, type HTMLAttributes, type ReactNode, type Ref, type RefObject } from "react";
import { nudgeScroll, registerStageScroller, relayTouches } from "../../lib/stage/scroller";
import { captureGhost, playGhost, sequenceArrival, type Ghost } from "../../lib/stage/handoff";
import { addressKey, poppedRecently, readScroll, restoreScroll, saveScroll } from "../../lib/stage/memory";
import { FOLD_ON, useLockFold } from "../../lib/os/lockFold";

const useIso = typeof window !== "undefined" ? useLayoutEffect : useEffect;

export interface StageProps {
  /** the navigator region. */
  head: ReactNode;
  /** a new value is a new navigator (a new level): it is handed over, not swapped. */
  headKey?: string;
  headRef?: RefObject<HTMLDivElement | null>;
  headClassName?: string;
  /** what the scene is: a new key is a new place (a handoff + a scroll reset/restore). */
  sceneKey: string;
  /** the way the directory moved to get here (+1 deeper / forward, −1 back). */
  dir: 1 | -1;
  /** the move was a swipe between siblings (the slower, spring-graced arrival). */
  swipe?: boolean;
  /** on arrival: the offset to restore (Back / Forward / refresh), or null for the top. */
  restoreTop?: number | null;
  /** the scroller's accessible name. */
  label?: string;
  /** end slack under the content (default 6rem). */
  slack?: string;
  /** how the split is held — see the header note. */
  fold?: "layer" | "split";
  /** "document" stages own the page (and the scroll bus); "contained" ones live inside a box. */
  scope?: "document" | "contained";
  /** a surface's own geometry for the seam (the reader's is tighter). */
  variant?: "reader";
  /** spread on the stage root (gesture binding, chrome fades). */
  rootProps?: HTMLAttributes<HTMLDivElement> & { style?: CSSProperties; inert?: boolean };
  rootRef?: Ref<HTMLDivElement>;
  scrollRef?: RefObject<HTMLDivElement | null>;
  /** false = the old view simply goes (no handoff). */
  handoff?: boolean;
  /** "shell" (the dashboard's column: --os-shell-max + gutter) or "none" (the
   *  surface brings its own column inside head and content). */
  frame?: "shell" | "none";
  /** where the navigator's first line sits (the fixed crumb bar's clearance). */
  top?: string;
  /** where it settles once folded, when that is higher than `top` (a public
   *  head used to travel up under the crumb bar before it pinned): the fold
   *  lifts it there, and the content's edge rises with it. */
  pin?: string;
  /** the room between the navigator's last row and the content's first line. */
  gap?: string;
  /** the stage folds its own navigator (lib/os/lockFold on the head, with the
   *  scroll-up reveal): every surface but the dashboard, which runs its own. */
  autoFold?: boolean;
  /** keep where each ADDRESS was scrolled to and put it back on Back/Forward
   *  (default: on for the surfaces the stage folds itself; the dashboard keeps
   *  its own per-entry memory). */
  remember?: boolean;
  children: ReactNode;
}

export default function Stage({
  head,
  headKey = "head",
  headRef: outerHeadRef,
  headClassName,
  sceneKey,
  dir,
  swipe = false,
  restoreTop = null,
  label = "content",
  slack,
  fold = "layer",
  scope = "document",
  variant,
  rootProps,
  rootRef: outerRootRef,
  scrollRef: outerScrollRef,
  handoff = true,
  frame = "shell",
  top,
  pin,
  gap,
  autoFold = false,
  remember,
  children,
}: StageProps) {
  const remembers = remember ?? autoFold;
  const innerScrollRef = useRef<HTMLDivElement>(null);
  const scrollRef = outerScrollRef ?? innerScrollRef;
  const innerHeadRef = useRef<HTMLDivElement>(null);
  const headRef = outerHeadRef ?? innerHeadRef;
  const ghostRef = useRef<HTMLDivElement>(null);
  const rootEl = useRef<HTMLDivElement | null>(null);
  const setRoot = (el: HTMLDivElement | null) => {
    rootEl.current = el;
    if (typeof outerRootRef === "function") outerRootRef(el);
    else if (outerRootRef) (outerRootRef as { current: HTMLDivElement | null }).current = el;
  };

  // the stage folds its own navigator on every surface but the dashboard: a
  // detached fold over the content (layer), or a real collapse that grows the
  // scroll area (split, the title-settling readers) — with the scroll-up reveal
  useLockFold(headRef, { scroller: scrollRef, room: fold === "split", active: autoFold });

  // a document stage IS the page's scroller while it stands (a contained one never is)
  useIso(() => {
    const el = scrollRef.current;
    if (!el || scope !== "document") return;
    return registerStageScroller(el);
  }, [scrollRef, scope]);

  // a vertical drag that starts on the navigator scrolls the content (it lies
  // over the content, outside its scroll chain — lib/stage/scroller)
  useEffect(() => {
    const h = headRef.current;
    if (!h) return;
    return relayTouches(h, () => scrollRef.current);
  }, [headRef, scrollRef]);

  // THE NAVIGATOR'S HEIGHT, for the content to stand under (layer). Its box
  // never changes on a fold (the fold is a fade and a ride), so this fires on a
  // new level, a longer line, a resize — never per frame of a fold.
  useIso(() => {
    const h = headRef.current;
    const root = rootEl.current;
    if (!h || !root || typeof ResizeObserver === "undefined") return;
    let last = -1;
    const write = () => {
      const v = Math.round(h.getBoundingClientRect().height * 10) / 10;
      if (v === last || v <= 0) return;
      last = v;
      root.style.setProperty("--stage-head-h", `${v}px`);
    };
    write();
    const ro = new ResizeObserver(write);
    ro.observe(h);
    return () => ro.disconnect();
  }, [headRef]);

  // THE PAGE HOLDS STILL WHILE A SPLIT NAVIGATOR FOLDS. The head is a row of
  // its own there, so its fold moves the scroll area's top edge; the lines being
  // read stay where they are and the fold uncovers the page above them, as the
  // old in-document head did (the browser's scroll anchoring held it). Never
  // across the fold line (the head would open again, and fold again), never as
  // a new place opens (its restored position is exact), and never under a
  // finger: a scroll set from script stops a touch glide, and a phone's browser
  // never anchored that page anyway.
  const settleUntil = useRef(0);
  useIso(() => {
    const h = headRef.current;
    const el = scrollRef.current;
    const root = rootEl.current;
    if (fold !== "split" || !autoFold || !h || !el || !root || typeof ResizeObserver === "undefined") return;
    let last = h.getBoundingClientRect().height;
    let touch = false;
    const byTouch = () => { touch = true; };
    const byHand = () => { touch = false; };
    const ro = new ResizeObserver(() => {
      const now = h.getBoundingClientRect().height;
      const d = now - last;
      last = now;
      if (!d || touch || performance.now() < settleUntil.current) return;
      // a fold gives back as much as the scroll can take without crossing the
      // line (all of it mid-page; a part near the top, where the page follows);
      // an open (a reveal mid-page) is held whole; an open at the top is not held
      const top = el.scrollTop;
      if (top < FOLD_ON) return;
      const next = d < 0 ? Math.max(FOLD_ON, top + d) : top + d;
      if (next !== top) nudgeScroll(el, next);
    });
    ro.observe(h);
    root.addEventListener("touchstart", byTouch, { passive: true });
    root.addEventListener("wheel", byHand, { passive: true });
    window.addEventListener("keydown", byHand);
    root.addEventListener("pointerdown", byHand);
    return () => {
      ro.disconnect();
      root.removeEventListener("touchstart", byTouch);
      root.removeEventListener("wheel", byHand);
      window.removeEventListener("keydown", byHand);
      root.removeEventListener("pointerdown", byHand);
    };
  }, [fold, autoFold, headRef, scrollRef]);

  // what is left of the screen for the content, for views that fill it (the
  // orbit). Written at once, then only once the size has SETTLED: it is an
  // inherited property, and restyling the whole content for each frame of a
  // resize (or of a split fold) would cost the very smoothness it is for.
  useIso(() => {
    const el = scrollRef.current;
    const root = rootEl.current;
    if (!el || !root || typeof ResizeObserver === "undefined") return;
    let last = -1;
    let t = 0;
    const write = () => {
      const h = el.clientHeight;
      if (h === last) return;
      last = h;
      root.style.setProperty("--stage-h", `${h}px`);
    };
    write();
    const ro = new ResizeObserver(() => {
      window.clearTimeout(t);
      t = window.setTimeout(write, 140);
    });
    ro.observe(el);
    return () => { ro.disconnect(); window.clearTimeout(t); };
  }, [scrollRef]);

  // where each address was scrolled to, for Back and Forward (lib/stage/memory)
  useEffect(() => {
    const el = scrollRef.current;
    if (!el || !remembers) return;
    const onScroll = () => saveScroll(addressKey(), el.scrollTop);
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, [scrollRef, remembers]);

  // a new place opens at its top; Back/Forward (and a refresh) open where it was
  const restoreRef = useRef(restoreTop);
  restoreRef.current = restoreTop ?? (remembers && poppedRecently() ? readScroll(addressKey()) : null);
  const firstScene = useRef(true);
  useIso(() => {
    const el = scrollRef.current;
    if (!el) return;
    // the new place's head settles on its own; the page is not held through it
    settleUntil.current = performance.now() + 450;
    const top = restoreRef.current;
    if (firstScene.current) {
      firstScene.current = false;
      if (top == null || top <= 0) return;
    }
    if (top != null && top > 0) return restoreScroll(el, top);
    el.scrollTop = 0;
  }, [sceneKey]);

  // keyboard scrolling lands on the scroller when focus was lost with the old
  // view (the relay in lib/stage/scroller covers focus anywhere else)
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const a = document.activeElement;
    if (!a || a === document.body || !a.isConnected) el.focus({ preventScroll: true });
  }, [sceneKey, scrollRef]);

  const { style: rootStyle0, className: rootClass, ...rest } = rootProps ?? {};
  const rootStyle = top || gap || pin
    ? ({
        ...rootStyle0,
        // the Mac app's inset rides on a surface's own top too (stage.css)
        ...(top ? { ["--stage-top" as string]: `calc(${top} + var(--stage-inset, 0px))` } : null),
        ...(gap ? { ["--stage-gap" as string]: gap } : null),
        ...(pin ? { ["--stage-pin-lift" as string]: `max(0px, calc(${top ?? "var(--stage-top)"} - ${pin}))` } : null),
      } as CSSProperties)
    : rootStyle0;
  return (
    <div
      ref={setRoot}
      className={`stage${rootClass ? ` ${rootClass}` : ""}`}
      data-stage
      data-fold={fold}
      data-frame={frame}
      data-scope={scope}
      data-variant={variant}
      style={rootStyle}
      {...rest}
    >
      {/* first in the document (the navigator is read and tabbed before the
          content); painted last (z 3), over the content's faded edge */}
      <div ref={headRef} className={`stage-head${headClassName ? ` ${headClassName}` : ""}`}>
        <HeadHandoff headKey={headKey} dir={dir} enabled={handoff}>
          <div
            key={headKey}
            className="stage-level"
            style={{ ["--enter-x" as string]: dir >= 0 ? "14px" : "-14px" } as CSSProperties}
          >
            {head}
          </div>
        </HeadHandoff>
      </div>
      <div
        ref={scrollRef}
        className="stage-scroll"
        data-stage-scroll
        tabIndex={-1}
        role="region"
        aria-label={label}
      >
        <div className="stage-clip">
          <Handoff
            handoffKey={sceneKey}
            dir={dir}
            enabled={handoff}
            layer={ghostRef}
            scroller={scrollRef}
            root={rootEl}
            className="stage-col"
            style={slack ? ({ ["--os-slack" as string]: slack } as CSSProperties) : undefined}
          >
            <div
              key={sceneKey}
              className="section-slide"
              data-swipe={swipe || undefined}
              style={{ ["--enter-x" as string]: dir >= 0 ? "14px" : "-14px" } as CSSProperties}
            >
              {children}
            </div>
          </Handoff>
        </div>
      </div>
      <div ref={ghostRef} className="stage-ghost" aria-hidden />
      <div className="stage-seam" aria-hidden />
    </div>
  );
}

// The content column, as a class for one reason: getSnapshotBeforeUpdate is the
// only moment React gives us BEFORE it replaces the old view's DOM — the copy
// for the handoff has to be taken there.
class Handoff extends Component<{
  handoffKey: string;
  dir: 1 | -1;
  enabled: boolean;
  layer: RefObject<HTMLDivElement | null>;
  scroller: RefObject<HTMLDivElement | null>;
  root: RefObject<HTMLDivElement | null>;
  className: string;
  style?: CSSProperties;
  children: ReactNode;
}> {
  col = createRef<HTMLDivElement>();

  getSnapshotBeforeUpdate(prev: { handoffKey: string }): Ghost | null {
    if (prev.handoffKey === this.props.handoffKey || !this.props.enabled) return null;
    const col = this.col.current;
    const sc = this.props.scroller.current;
    if (!col || !sc) return null;
    return captureGhost(col, sc, this.props.root.current);
  }

  componentDidUpdate(prev: { handoffKey: string }, _s: unknown, snap: Ghost | null) {
    // only a NEW place changes the handoff; any other render leaves it alone
    // (the shell re-renders many times right after a move, and clearing the hold
    // on the next one let the new view in at once, over the old)
    if (prev.handoffKey === this.props.handoffKey) return;
    const layer = this.props.layer.current;
    const col = this.col.current;
    if (snap && layer) {
      const leaving = playGhost(snap, layer, this.props.dir);
      if (col) sequenceArrival(col, leaving);
    } else if (col) {
      col.removeAttribute("data-handoff");
    }
  }

  render() {
    return (
      <div ref={this.col} className={this.props.className} style={this.props.style}>
        {this.props.children}
      </div>
    );
  }
}

// The navigator's handoff: at a new level the old navigator's words leave
// toward the side the directory left by while the new ones arrive, on the same
// timings as the content. (Between siblings the navigator stays and its ticker
// glides; that is not a handoff.)
class HeadHandoff extends Component<{ headKey: string; dir: 1 | -1; enabled: boolean; children: ReactNode }> {
  box = createRef<HTMLDivElement>();
  ghost = createRef<HTMLDivElement>();

  getSnapshotBeforeUpdate(prev: { headKey: string }): Ghost | null {
    if (prev.headKey === this.props.headKey || !this.props.enabled) return null;
    const box = this.box.current;
    if (!box) return null;
    return captureGhost(box, null, null);
  }

  componentDidUpdate(prev: { headKey: string }, _s: unknown, snap: Ghost | null) {
    if (prev.headKey === this.props.headKey) return;
    const layer = this.ghost.current;
    const box = this.box.current;
    if (snap && layer) {
      const leaving = playGhost(snap, layer, this.props.dir);
      if (box) sequenceArrival(box, leaving);
    } else if (box) {
      box.removeAttribute("data-handoff");
    }
  }

  render() {
    return (
      <div className="stage-head-box">
        <div ref={this.box} className="stage-head-in">
          {this.props.children}
        </div>
        <div ref={this.ghost} className="stage-head-ghost" aria-hidden />
      </div>
    );
  }
}
